import React, { useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    makeStyles,
    Dialog,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const useStyles = makeStyles((theme) => ({
    uploadBox: {
        border: "2px dashed #bbb",
        borderRadius: 8,
        padding: theme.spacing(3),
        textAlign: "center",
        cursor: "pointer",
        color: "#666",
        backgroundColor: "#fafafa",
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: "#f0f0f0",
        },
    },
    buttonSpacing: {
        marginLeft: theme.spacing(2),
    },
    previewsContainer: {
        display: "flex",
        flexWrap: "wrap",
        marginTop: theme.spacing(2),
        gap: theme.spacing(2),
    },
    previewCard: {
        width: 160,
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    closeBtn: {
        position: "absolute",
        top: 4,
        right: 4,
        backgroundColor: "#ffffffcc",
        "&:hover": {
            backgroundColor: "#ffffff",
        },
        zIndex: 2,
    },
    imgPreview: {
        width: "100%",
        height: 120,
        objectFit: "cover",
        borderBottom: "1px solid #ddd",
        cursor: "pointer",
    },
    pdfPreview: {
        height: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #ddd",
    },
    fileDetails: {
        padding: theme.spacing(1),
        textAlign: "center",
    },
}));

const OMRUploader = () => {
    const classes = useStyles();
    const [files, setFiles] = useState([]);
    const [openPreview, setOpenPreview] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    const handleFilesChange = (e) => {
        if (!e.target.files) return;
        const arr = Array.from(e.target.files).map((f) => ({
            file: f,
            previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : "",
        }));
        setFiles((prev) => {
            const all = [...prev, ...arr];
            const uniq = all.filter(
                (v, i, a) =>
                    a.findIndex(
                        (x) =>
                            x.file.name === v.file.name && x.file.size === v.file.size
                    ) === i
            );
            return uniq;
        });
        e.target.value = "";
    };

    const handleRemove = (idx) => {
        setFiles((prev) => {
            if (prev[idx].previewUrl) URL.revokeObjectURL(prev[idx].previewUrl);
            return prev.filter((_, i) => i !== idx);
        });
    };

    const handlePreview = (url) => {
        setSelectedImage(url);
        setOpenPreview(true);
    };

    const handleOMRSubmit = async () => {
        if (files.length === 0) {
            toast.warn("Please upload at least one file.");
            return;
        }
        const formData = new FormData();
        files.forEach(({ file }) => {
            if (file.type.startsWith("image/")) {
                formData.append("omr_sheets[]", file);
            }
        });

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/v1/omr/upload`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            toast.success("OMR sheets uploaded successfully!");
            setFiles([]);
        } catch (err) {
            console.error(err);
            toast.error("There was an issue with the API.");
        }
    };

    return (
        <>
            <ToastContainer />
            <input
                accept="image/*,application/pdf"
                id="omr-uploader-input"
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFilesChange}
            />
            <label htmlFor="omr-uploader-input">
                <Box className={classes.uploadBox}>
                    <CloudUploadIcon fontSize="large" />
                    <br />
                    Click here or drag & drop to upload OMR sheet
                    <br />
                    (JPEG / PNG / PDF)
                </Box>
            </label>

            <Box className={classes.previewsContainer}>
                {files.map((f, idx) => (
                    <Card key={idx} className={classes.previewCard} sx={{marginBottom:'2rem'}}>
                        <IconButton
                            size="small"
                            className={classes.closeBtn}
                            onClick={() => handleRemove(idx)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        {f.file.type === "application/pdf" ? (
                            <Box className={classes.pdfPreview}>
                                <PictureAsPdfIcon style={{ fontSize: 48, color: "#d32f2f" }} />
                            </Box>
                        ) : (
                            <img
                                src={f.previewUrl}
                                alt={f.file.name}
                                className={classes.imgPreview}
                                onClick={() => handlePreview(f.previewUrl)}
                            />
                        )}
                        <Box className={classes.fileDetails}>
                            <Typography variant="body2" noWrap>
                                {f.file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {f.file.type.split("/")[1].toUpperCase()},{" "}
                                {(f.file.size / 1024).toFixed(1)} KB
                            </Typography>
                        </Box>
                    </Card>
                ))}
            </Box>

            <Box sx={{marginTop:'2rem'}} display="flex" justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setFiles([])}>
                    Reset All
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOMRSubmit}
                    className={classes.buttonSpacing}
                >
                    Submit
                </Button>
            </Box>


            {/* Dialog Preview */}
            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md">
                <img
                    src={selectedImage}
                    alt="Preview"
                    style={{ width: "40vh", height: "auto", maxHeight: "90vh" }}
                />
            </Dialog>
        </>
    );
};

export default OMRUploader;
